import { useEffect, useRef, useState } from "react";
import { authService } from "services";
import { isHaddockEnvironment } from "utils/documentUtils";
import { isHaddockZendeskHostname } from "utils/haddockDomain";
import { getMessagingToken } from "services/zendeskService";
import { getHaddockActorProps, trackHaddockEvent } from "utils/posthogTracking";
import {
	applyZendeskMessengerContext,
	buildZendeskContextSignature,
	hideZendeskMessenger,
	loadZendeskMessagingScript,
	remountZendeskMessenger,
} from "utils/zendeskUtils";
import {
	authenticateZendeskMessenger,
	bindZendeskOpenGuard,
	getAcceptedZendeskSession,
	getZendeskIdentityKey,
	invalidateZendeskMessengerSession,
	isZendeskTokenFresh,
	onRemoteZendeskIdentityInvalidated,
	shouldRefreshZendeskAuth,
	subscribeZendeskIdentityInvalidation,
	withZendeskAuthLock,
} from "utils/zendeskAuth";

const ZENDESK_LOGIN_MAX_ATTEMPTS = 3;
const ZENDESK_LOGIN_RETRY_DELAY_MS = 1500;
/** Tras SSO/cookie hay más tráfico de red (refresh, suscripción); dar margen antes del JWT. */
const ZENDESK_SSO_WARMUP_MS = 400;

async function fetchZendeskMessagingToken() {
	if (!authService.token?.access_token) {
		throw new Error("Sesión Plain no lista para Zendesk Messaging");
	}
	const response = await getMessagingToken();
	if (!response?.ok || !response?.access_token) {
		throw new Error("No se pudo obtener el token de Zendesk Messaging");
	}
	return response.access_token;
}

function delay(ms) {
	return new Promise((resolve) => {
		window.setTimeout(resolve, ms);
	});
}

function ZendeskWidgetInner({ user }) {
	const [scriptReady, setScriptReady] = useState(
		typeof window !== "undefined" && typeof window.zE === "function"
	);
	const [messengerEpoch, setMessengerEpoch] = useState(0);
	const contextSignatureRef = useRef("");
	const userRef = useRef(user);
	const runSyncRef = useRef(async () => {});
	userRef.current = user;

	useEffect(() => {
		let cancelled = false;

		if (scriptReady) return undefined;

		loadZendeskMessagingScript()
			.then(() => {
				if (cancelled) return;
				// Ocultar hasta completar loginUser: evita conversación anónima.
				hideZendeskMessenger();
				setScriptReady(true);
			})
			.catch((error) => {
				console.error(
					"[Zendesk] No se pudo cargar el widget. ¿REACT_APP_ZENDESK_WIDGET_KEY configurada? Reinicia el dev server tras cambiar .env.haddock.",
					error
				);
			});

		return () => {
			cancelled = true;
		};
	}, [scriptReady]);

	useEffect(() => {
		if (!scriptReady || typeof window.zE !== "function") {
			return undefined;
		}

		return bindZendeskOpenGuard({
			isAllowed: () => {
				const currentUser = userRef.current;
				const session = getAcceptedZendeskSession();
				return (
					!!currentUser?.id &&
					session.confirmed &&
					session.identityKey === getZendeskIdentityKey(currentUser) &&
					isZendeskTokenFresh(session.token)
				);
			},
			onBlocked: () => {
				runSyncRef.current?.();
			},
		});
	}, [scriptReady, messengerEpoch]);

	useEffect(() => {
		if (!scriptReady || typeof window.zE !== "function") {
			return undefined;
		}

		if (!user?.id || !user?.email) {
			if (contextSignatureRef.current || getAcceptedZendeskSession().confirmed) {
				invalidateZendeskMessengerSession("user_missing");
				contextSignatureRef.current = "";
			}
			return undefined;
		}

		let cancelled = false;
		const contextSignature = buildZendeskContextSignature(user);

		const trackAuthFailure = (error) => {
			trackHaddockEvent("support__zendesk_auth__failed__widget", {
				...getHaddockActorProps(user),
				attempts: 1,
				reason: "login_failed",
				error_message: error?.message || (error ? String(error) : null),
			});
		};

		/**
		 * Si loginUser falla (JWT inválido, 409 email conflict, timeout…):
		 * - no abrir en modo anónimo
		 * - remountar para salir del spinner infinito de Zendesk
		 * - dejar el widget oculto
		 */
		const handleFailedLogin = async (error) => {
			console.error("Error sincronizando Zendesk Messaging:", error);
			trackAuthFailure(error);
			// Evita reintentos en bucle con el mismo contexto.
			contextSignatureRef.current = contextSignature;

			try {
				await withZendeskAuthLock(async () => {
					invalidateZendeskMessengerSession("auth_failed");
					await remountZendeskMessenger();
				});
				if (!cancelled) {
					hideZendeskMessenger();
					setScriptReady(typeof window.zE === "function");
					setMessengerEpoch((epoch) => epoch + 1);
				}
			} catch (remountError) {
				console.error(
					"[Zendesk] No se pudo remountar el messenger tras fallo de auth:",
					remountError
				);
				hideZendeskMessenger();
			}
		};

		const syncZendeskSession = async () => {
			const currentUser = userRef.current;
			if (!currentUser?.id || !currentUser?.email) return;

			const session = getAcceptedZendeskSession();
			const currentSignature = buildZendeskContextSignature(currentUser);
			if (
				contextSignatureRef.current === currentSignature &&
				session.confirmed &&
				session.identityKey === getZendeskIdentityKey(currentUser) &&
				isZendeskTokenFresh(session.token)
			) {
				applyZendeskMessengerContext(currentUser);
				return;
			}

			if (authService.isFromHaddock) {
				await delay(ZENDESK_SSO_WARMUP_MS);
			}

			for (let attempt = 1; attempt <= ZENDESK_LOGIN_MAX_ATTEMPTS; attempt++) {
				if (cancelled) return;

				if (attempt > 1) {
					await delay(ZENDESK_LOGIN_RETRY_DELAY_MS * attempt);
				}

				try {
					hideZendeskMessenger();

					const result = await authenticateZendeskMessenger({
						user: currentUser,
						fetchToken: fetchZendeskMessagingToken,
						isStale: () => cancelled,
					});

					if (cancelled) return;
					if (result?.status === "cancelled") return;

					contextSignatureRef.current = buildZendeskContextSignature(
						currentUser
					);
					return;
				} catch (error) {
					if (cancelled) return;
					if (attempt < ZENDESK_LOGIN_MAX_ATTEMPTS) {
						console.warn(
							`[Zendesk] Intento ${attempt}/${ZENDESK_LOGIN_MAX_ATTEMPTS} fallido, reintentando…`,
							error
						);
						continue;
					}
					await handleFailedLogin(error);
				}
			}
		};

		runSyncRef.current = syncZendeskSession;
		syncZendeskSession();

		return () => {
			cancelled = true;
		};
	}, [user, scriptReady]);

	useEffect(() => {
		if (!scriptReady) return undefined;

		const onVisibility = () => {
			if (document.visibilityState !== "visible") return;
			const currentUser = userRef.current;
			if (!shouldRefreshZendeskAuth(currentUser)) return;
			runSyncRef.current?.();
		};

		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [scriptReady]);

	useEffect(() => {
		if (!scriptReady) return undefined;

		return subscribeZendeskIdentityInvalidation(() => {
			onRemoteZendeskIdentityInvalidated();
			contextSignatureRef.current = "";
			runSyncRef.current?.();
		});
	}, [scriptReady]);

	return null;
}

export default function ZendeskWidget({ user }) {
	const hostname =
		typeof window !== "undefined" ? window.location.hostname : "";
	// El stage (st-hr.haddock.app) tiene look Haddock, pero sin chat.
	if (!isHaddockEnvironment() || !isHaddockZendeskHostname(hostname)) {
		return null;
	}

	return <ZendeskWidgetInner user={user} />;
}
